import { log } from '@/log'
import { SelectOptionList } from '@/models/api/payment/parameter/type/user-select'
import { Payment as AvailablePayment, PaymentList } from '@/models/api/payment/payment'
import CreateSelectOptionList from '@/services/payment/create-select-option-list'
import {
  buildCreditcardPayment,
  buildWithStaticValue,
} from '@spa/components/pages/order/model/payment/initialization/builder'
import {
  PaymentInitializationSpec,
  ResultPaymentInitializationSpec,
} from '@spa/components/pages/order/model/payment/initialization/specification'
import { PurchaseService, SalesOrder } from '@spa/services/api/purchase'
import { CreditcardModel } from '@spa/store/modules/creditcard/types'
import { isCreditCard, PaymentWithApiPayment } from '@spa/store/modules/orderConfirm/types'
import * as E from 'fp-ts/lib/Either'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'

export type LatestPurchaseGateway = Pick<PurchaseService, 'latest'>
export type AvailablePaymentList = Pick<PaymentList, 'find'>
export type CreateSelectOptionListFromPaymentMethod = Pick<CreateSelectOptionList, 'fromPaymentMethod'>
export interface CreditcardStore {
  readonly first: CreditcardModel | undefined
  findById(id: string): CreditcardModel | undefined
}

export class UsePreviousPaymentMethod implements PaymentInitializationSpec {
  public static create(
    purchaseService: LatestPurchaseGateway,
    availablePaymentList: AvailablePaymentList,
    creditcardStore: CreditcardStore
  ): PaymentInitializationSpec {
    return new UsePreviousPaymentMethod(
      purchaseService,
      availablePaymentList,
      creditcardStore,
      CreateSelectOptionList.default()
    )
  }

  /**
   * @internal テストなどで利用する内部用.プロダクトコードでは{@link UsePreviousPaymentMethod.create}を使用すること.
   */
  constructor(
    private readonly purchaseService: LatestPurchaseGateway,
    private readonly availablePaymentList: AvailablePaymentList,
    private readonly creditcardStore: CreditcardStore,
    private readonly createSelectOptionList: CreateSelectOptionListFromPaymentMethod
      = CreateSelectOptionList.default()
  ) {}

  async build(): Promise<ResultPaymentInitializationSpec> {
    try {
      const latestResult = await this.purchaseService.latest()

      return latestResult.map(maybeLatestPurchase => maybeLatestPurchase.foldL(
        (): O.Option<PaymentWithApiPayment> => O.fromNullable(this.creditcardStore.first).chain(
          card =>
            O.fromNullable(this.availablePaymentList.find(p => p.isCreditCard()))
            .chain(availableCreditCardPayment => availableCreditCardPayment.isCreditCard() // 型推論の都合上、分岐を追加している
              ? O.some(buildCreditcardPayment(availableCreditCardPayment, card))
              : O.none
            )
        ),
        latestPurchase => pipe(
          this.findUsedPaymentFor(latestPurchase),
          O.chain((payment) => {
            const paymentMethod = payment.id
            switch (paymentMethod) {
              case 'cash-on-delivery':
              case 'np-postpay':
              case 'atone':
              case 'paidy':
                return O.some(buildWithStaticValue(paymentMethod, payment))
              case 'creditcard':
              case 'creditcard3d':
                return O.fromNullable(latestPurchase.payment.method.token)
                  .mapNullable(creditcardId =>
                    this.creditcardStore.findById(creditcardId) || this.creditcardStore.first
                  )
                  .map(c => buildCreditcardPayment(payment, c))
              default:
                // ユーザーの選択したオプション（コンビニの種別とか）を含めて
                // 2023/1/20時点では、コンビニ決済とキャリア決済で利用
                // 今後はこちらに寄せていくかも
                return this.createSelectOptionList.fromPaymentMethod(latestPurchase.payment.method, payment).fold(
                  (error: Error) => {
                    log.warn({
                      message: 'could not create select option list from purchase',
                      error,
                    })

                    return O.none
                  },
                  (selectedOptionList: SelectOptionList) => O.some({
                    payment_type: paymentMethod,
                    apiPayment: payment,
                    selectedOptionList,
                  })
                )
            }
          })
        )
      ))
    } catch (e) {
      return E.left(e)
    }
  }

  private findUsedPaymentFor(order: SalesOrder): O.Option<AvailablePayment> {
    return O.fromNullable(this.availablePaymentList.find((p) => {
      const usedMethodLatest = order.payment.method.kind
      if (usedMethodLatest === 'none') return false

      if (p.isCreditCard()) {
        /**
         * 3Dセキュアが有効な環境であっても、以下のパターンでは3Dセキュア無しの決済が実施される場合がある
         * - 予約商品と通常商品を一度に注文するなど、注文が分割されるケースでクレジットカード決済（予定）
         *
         * その場合でも注文確認画面で「前回はクレジットカードを使用した」と評価するために、
         * ここでは3Dセキュアと非3Dセキュアを意図的に無視して判定している.
         *
         * @see https://github.com/Everforth/growthpalette-front/pull/7869#issuecomment-1910401334
         * @see https://github.com/Everforth/growthpalette-front/issues/7851
         * @see https://github.com/Everforth/growthpalette-pm/issues/277
         * @note
         * 3Dセキュアと非3Dセキュアを区別する必要があるシーンと区別したくないシーンが併存しているので、整理が必要.
         * 区別する必要があるシーンとして、実際の決済リクエスト生成と決済時の事後処理がある.
         */
        return isCreditCard({ payment_type: usedMethodLatest })
      }

      return usedMethodLatest === p.id
    }))
  }
}
